/**
 * IsobarWebGLLayer.tsx — غلاف React لطبقة خطوط تساوي الضغط (‏IsobarGLLayer).
 *
 * الطبقة تقرأ نسيج الضغط بنفسها، فتعمل فوق أي حقل ملوّن: رياح، حرارة، أمطار، ضغط.
 */
import { useEffect, useRef } from 'react';
import { useMapRef } from './MapContext';
import { blendFor, getPlaybackPosition, subscribePlayback } from '../../services/playbackClock';
import { IsobarGLLayer } from './webgl/IsobarGLLayer';
import { getWeatherInsertBeforeId, raiseParticlesAboveWeatherLayers, ISOBAR_LAYER_ID } from './webgl/layerOrder';

interface Props {
    url: string;
    /** الإطار التالي للاستيفاء الزمني. */
    urlNext?: string;
    /** فهرس الإطار الذي يمثّله `url` — تمريره يفعّل المزج المستمرّ من ساعة التشغيل. */
    frameIndex?: number;
    /** النسيج نظير 16-بت؟ */
    p16: boolean;
    /** لون الخطّ. أبيض دائماً كما عند Zoom Earth — راجع تعليل الاستدعاء. */
    color: [number, number, number];
    opacity?: number;
}

export function IsobarWebGLLayer({ url, urlNext = '', frameIndex, p16, color, opacity = 0.92 }: Props) {
    const mapRef = useMapRef();
    const layerRef = useRef<IsobarGLLayer | null>(null);

    // أحدث القيم عبر refs: الإضافة تتأخّر حتى جهوز الستايل، فقد يتغيّر الإطار/اللون
    // قبلها — وبلا هذا تعلَق الطبقة على قيم لحظة الإنشاء.
    const urlRef = useRef(url); urlRef.current = url;
    const urlNextRef = useRef(urlNext); urlNextRef.current = urlNext;
    const p16Ref = useRef(p16); p16Ref.current = p16;
    const colorRef = useRef(color); colorRef.current = color;
    const opacityRef = useRef(opacity); opacityRef.current = opacity;
    const blendRef = useRef(0);
    blendRef.current = frameIndex !== undefined ? blendFor(getPlaybackPosition(), frameIndex) : 0;

    useEffect(() => {
        const wrap = mapRef.current;
        if (!wrap) return;
        const map = wrap.getMap();
        let cancelled = false;
        const layer = new IsobarGLLayer(ISOBAR_LAYER_ID, url);

        const add = () => {
            if (cancelled) return;
            try {
                layer.setP16(p16Ref.current);
                layer.setColor(colorRef.current);
                layer.setOpacity(opacityRef.current);
                layer.setUrl(urlRef.current);
                layer.setNextUrl(urlNextRef.current);
                layer.setBlend(blendRef.current);
                if (!map.getLayer(ISOBAR_LAYER_ID)) map.addLayer(layer, getWeatherInsertBeforeId(map));
                raiseParticlesAboveWeatherLayers(map);
                layerRef.current = layer;
            } catch (e) { console.error('IsobarWebGLLayer', e); }
        };
        let intervalId: number | undefined;
        const onStyleData = () => { if (map.isStyleLoaded()) { cleanupWait(); add(); } };
        const cleanupWait = () => { map.off('styledata', onStyleData); if (intervalId !== undefined) window.clearInterval(intervalId); };
        if (map.isStyleLoaded()) add();
        else { map.on('styledata', onStyleData); intervalId = window.setInterval(() => { if (map.isStyleLoaded()) { cleanupWait(); add(); } }, 150); }

        return () => {
            cancelled = true; cleanupWait(); layerRef.current = null;
            try { if (map.getLayer(ISOBAR_LAYER_ID)) map.removeLayer(ISOBAR_LAYER_ID); } catch { /* تجاهل */ }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mapRef]);

    useEffect(() => { layerRef.current?.setUrl(url); }, [url]);
    useEffect(() => { layerRef.current?.setNextUrl(urlNext); }, [urlNext]);
    useEffect(() => { layerRef.current?.setP16(p16); }, [p16]);
    useEffect(() => { layerRef.current?.setColor(color); }, [color[0], color[1], color[2]]);
    useEffect(() => { layerRef.current?.setOpacity(opacity); }, [opacity]);

    /*
     * المزج المستمرّ — نفس قناة طبقات الألوان: تشترك الطبقة في ساعة التشغيل وتضبط
     * uniform المزج كل نبضة رسم متجاوزةً React. بلا هذا تقفز الخطوط بالساعة بينما
     * ينساب اللون تحتها، فتُرى الخطوط منفصلةً عن الحقل الذي هي منه.
     */
    useEffect(() => {
        if (frameIndex === undefined) return;
        return subscribePlayback((p) => { layerRef.current?.setBlend(blendFor(p, frameIndex)); });
    }, [frameIndex]);

    return null;
}
